// ./src/components/features/Reminders/ReminderTemplates/ReminderTemplates.tsx
//
// "Ghost" starter templates — dashed placeholder cards shown for any reminder kind the host hasn't set
// up yet. Tapping one opens the add drawer pre-filled from the preset, so a host can start from a
// sensible default and just tweak. Mirrors the empty-state affordance but stays useful once some
// reminders exist (it only shows the kinds still missing).

import React from "react";
import { SimpleGrid, Paper, Group, Stack, Text, Badge, ThemeIcon, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";

import { ReminderPreset } from "../reminderTemplates";
import { kindLabel, kindColor } from "../reminderDisplay";

export interface ReminderTemplatesProps {
  presets: ReminderPreset[];
  onUse: (preset: ReminderPreset) => void;
}

const ReminderTemplatesComponent: React.FC<ReminderTemplatesProps> = ({ presets, onUse }) => {
  const { t } = useTranslation(["events"]);

  if (presets.length === 0) {
    return null;
  }

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed" fw={600}>
        {t("events:reminders.templates.heading", { defaultValue: "Start from a template" })}
      </Text>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
        {presets.map((preset) => (
          <Paper
            key={preset.presetId}
            p="md"
            radius="md"
            withBorder
            onClick={() => onUse(preset)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onUse(preset);
              }
            }}
            style={{
              cursor: "pointer",
              borderStyle: "dashed",
              transition: "border-color 150ms ease, background-color 150ms ease",
            }}
            className="em-ghost-card"
          >
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <ThemeIcon variant="light" color={kindColor(preset.reminder.kind)} radius="xl" size={40}>
                <AppIcon iconName={preset.iconName} size={20} />
              </ThemeIcon>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Group gap={6} wrap="nowrap">
                  <Text size="md" fw={600} lineClamp={1}>
                    {preset.reminder.name}
                  </Text>
                </Group>
                <Box mt={4}>
                  <Badge size="xs" variant="light" color={kindColor(preset.reminder.kind)}>
                    {kindLabel(preset.reminder.kind)}
                  </Badge>
                </Box>
                <Text size="sm" c="dimmed" mt={6} lineClamp={2}>
                  {preset.description}
                </Text>
              </Box>
              <AppIcon iconName="Plus" size={18} />
            </Group>
          </Paper>
        ))}
      </SimpleGrid>
    </Stack>
  );
};

export const ReminderTemplates = React.memo(ReminderTemplatesComponent);
